import React, { useState, useEffect, useCallback } from 'react';
import { ProductDetail, TemperatureRequirements, TemperatureRange, TemperatureValue, Procedure } from '../../../../types/ProductTypes';
import { useEditMode } from '../../../../context/EditModeContext';
import { useAuthStore } from '../../../../context/AuthContext';
import { useProductEdit } from '../../../../context/ProductEditContext';
import { SectionCard, SectionTitle, InfoGrid, InfoItem, FullWidthFields, FullWidthItem, ProcedureItem, DeleteButton } from '../../Section.styles';
import { RangeInputWrapper, CompactRangeInput45, UnitLabel35, EditableTextarea } from '../../EditableFields.styles';
import { ProcedureHeader, ProcedureSelect } from './OperationalHeatingSection.styles';
import useGetProcedures from '../../../../api/useGetProcedures';
import useGetProcedureDetails from '../../../../api/useGetProcedureDetails';
import { PROCEDURE_CATEGORIES } from '../../../../types/ProcedureTypes';

/**
 * Calculates the maximum adjacent temperature based on boiling point and USCG group.
 * This is a reference calculation to help users determine appropriate values.
 */
const calculateMaxAdjacentTemperature = (boilingPoint: number | null | undefined, uscgGroup: number | null | undefined): number => {
    const lowerTemperatureLimit = 35;
    const upperTemperatureLimit = 100;
    const defaultTemperature = 0;
    let calculatedMaxAdjacentTemperature = defaultTemperature;

    // Check if we have uscgGroup - fixed-value groups don't require boilingPoint
    if (uscgGroup !== undefined && uscgGroup !== null) {
        // Fixed value groups - don't require boiling point
        if ([0, 16, 42, 43].includes(uscgGroup)) {
            calculatedMaxAdjacentTemperature = 35;
        } else if ([2, 4, 37].includes(uscgGroup)) {
            calculatedMaxAdjacentTemperature = 40;
        } else if ([1, 3, 5, 8, 12, 22, 34, 40].includes(uscgGroup)) {
            calculatedMaxAdjacentTemperature = 50;
        } else if (boilingPoint !== undefined && boilingPoint !== null) {
            // Calculated groups - require boiling point
            let reactivityFactor: number;
            switch (uscgGroup) {
                case 20:
                case 41:
                    reactivityFactor = 20;
                    break;
                case 13:
                case 15:
                    reactivityFactor = 45;
                    break;
                case 7:
                case 36:
                    reactivityFactor = 50;
                    break;
                case 10:
                case 11:
                case 17:
                case 18:
                    reactivityFactor = 85;
                    break;
                case 19:
                case 30:
                    reactivityFactor = 100;
                    break;
                case 9:
                    reactivityFactor = 110;
                    break;
                case 14:
                    reactivityFactor = 160;
                    break;
                default:
                    reactivityFactor = 0;
                    break;
            }

            calculatedMaxAdjacentTemperature = boilingPoint - 20 - reactivityFactor;
            calculatedMaxAdjacentTemperature = Math.min(calculatedMaxAdjacentTemperature, upperTemperatureLimit);
            calculatedMaxAdjacentTemperature = Math.max(calculatedMaxAdjacentTemperature, lowerTemperatureLimit);
        }
    }

    return calculatedMaxAdjacentTemperature;
};

interface OperationalHeatingSectionProps {
    product: ProductDetail;
    renderTemperatureRange: (range: any) => string;
}

const OperationalHeatingSection: React.FC<OperationalHeatingSectionProps> = ({ product, renderTemperatureRange }) => {
    const { isInEditMode } = useEditMode();
    const { canEdit } = useAuthStore();
    const productEditContext = useProductEdit();
    const mergePendingChanges = productEditContext?.mergePendingChanges;
    const getPendingChanges = productEditContext?.getPendingChanges;
    const showEditUI = canEdit && isInEditMode;

    // Procedure loading hooks
    const { procedures: availableProcedures, getProcedures, loading: proceduresLoading } = useGetProcedures(PROCEDURE_CATEGORIES.OPERATIONAL);
    const { getProcedureDetails } = useGetProcedureDetails();
    const [selectedProcedureId, setSelectedProcedureId] = useState<string>('');

    // Load available procedures when in edit mode
    useEffect(() => {
        if (showEditUI) {
            getProcedures();
        }
    }, [showEditUI, getProcedures]);

    // Form state for temperature fields
    const [formData, setFormData] = useState({
        loadingTempMin: product.temperatureRequirements?.loadingTemperatureRange?.minValue?.value?.toString() || '',
        loadingTempMax: product.temperatureRequirements?.loadingTemperatureRange?.maxValue?.value?.toString() || '',
        voyageTempMin: product.temperatureRequirements?.voyageTemperatureRange?.minValue?.value?.toString() || '',
        voyageTempMax: product.temperatureRequirements?.voyageTemperatureRange?.maxValue?.value?.toString() || '',
        unloadingTempMin: product.temperatureRequirements?.unloadingTemperatureRange?.minValue?.value?.toString() || '',
        unloadingTempMax: product.temperatureRequirements?.unloadingTemperatureRange?.maxValue?.value?.toString() || '',
        maxAdjacentTemp: product.temperatureRequirements?.maxAdjacentTemperature?.value?.toString() || '',
        operationalCommentText: product.comments?.find((c) => c.category === 3)?.text || '',
        msgCommentText: product.comments?.find((c) => c.category === 13)?.text || '',
    });

    // Sync form state when product changes
    useEffect(() => {
        setFormData({
            loadingTempMin: product.temperatureRequirements?.loadingTemperatureRange?.minValue?.value?.toString() || '',
            loadingTempMax: product.temperatureRequirements?.loadingTemperatureRange?.maxValue?.value?.toString() || '',
            voyageTempMin: product.temperatureRequirements?.voyageTemperatureRange?.minValue?.value?.toString() || '',
            voyageTempMax: product.temperatureRequirements?.voyageTemperatureRange?.maxValue?.value?.toString() || '',
            unloadingTempMin: product.temperatureRequirements?.unloadingTemperatureRange?.minValue?.value?.toString() || '',
            unloadingTempMax: product.temperatureRequirements?.unloadingTemperatureRange?.maxValue?.value?.toString() || '',
            maxAdjacentTemp: product.temperatureRequirements?.maxAdjacentTemperature?.value?.toString() || '',
            operationalCommentText: product.comments?.find((c) => c.category === 3)?.text || '',
            msgCommentText: product.comments?.find((c) => c.category === 13)?.text || '',
        });
    }, [product]);

    // Helper to parse temperature value
    const parseTemperatureValue = (value: string): TemperatureValue | null => {
        if (value === '' || value === null || value === undefined) return null;
        const parsed = parseFloat(value);
        if (isNaN(parsed)) return null;
        return { value: parsed };
    };

    // Helper to create temperature range
    const createTemperatureRange = (minValue: string, maxValue: string): TemperatureRange => {
        return {
            minValue: parseTemperatureValue(minValue),
            maxValue: parseTemperatureValue(maxValue),
        };
    };

    // Update temperature requirements in context
    const updateTemperatureRequirements = useCallback(
        (field: string, value: string) => {
            setFormData((prev) => ({ ...prev, [field]: value }));

            if (!mergePendingChanges) return;

            // Get current form data with the new value
            const updatedFormData = { ...formData, [field]: value };

            const temperatureRequirements: TemperatureRequirements = {
                loadingTemperatureRange: createTemperatureRange(updatedFormData.loadingTempMin, updatedFormData.loadingTempMax),
                voyageTemperatureRange: createTemperatureRange(updatedFormData.voyageTempMin, updatedFormData.voyageTempMax),
                unloadingTemperatureRange: createTemperatureRange(updatedFormData.unloadingTempMin, updatedFormData.unloadingTempMax),
                maxAdjacentTemperature: parseTemperatureValue(updatedFormData.maxAdjacentTemp),
            };

            mergePendingChanges({ temperatureRequirements });
        },
        [formData, mergePendingChanges]
    );

    // Update comments in context
    const updateComment = useCallback(
        (category: number, value: string) => {
            const fieldName = category === 3 ? 'operationalCommentText' : 'msgCommentText';
            setFormData((prev) => ({ ...prev, [fieldName]: value }));

            if (!mergePendingChanges || !getPendingChanges) return;

            const currentPendingChanges = getPendingChanges();
            const baseComments = currentPendingChanges.comments || product.comments || [];
            const existingComment = product.comments?.find((c) => c.category === category);

            // Keep all comments except the one we're updating
            const otherComments = baseComments.filter((c: any) => c.category !== category);

            const updatedComments = [
                ...otherComments,
                {
                    id: existingComment?.id || 0,
                    text: value,
                    category: category,
                    visibility: existingComment?.visibility || 1,
                    lastUpdatedUtc: new Date().toISOString(),
                },
            ];

            mergePendingChanges({ comments: updatedComments as any });
        },
        [product.comments, mergePendingChanges, getPendingChanges]
    );

    // Handle procedure removal
    const removeProcedure = useCallback(
        (procedureId: number) => {
            if (!mergePendingChanges || !getPendingChanges) return;

            const currentPendingChanges = getPendingChanges();
            const baseProcedures = currentPendingChanges.procedures || product.procedures || [];
            const updatedProcedures = baseProcedures.filter((p: any) => p.id !== procedureId);

            mergePendingChanges({ procedures: updatedProcedures as any });
        },
        [product.procedures, mergePendingChanges, getPendingChanges]
    );

    // Handle adding a procedure
    const addProcedure = useCallback(
        async (procedureId: string) => {
            if (!procedureId || !mergePendingChanges || !getPendingChanges) return;

            try {
                const response = await getProcedureDetails(PROCEDURE_CATEGORIES.OPERATIONAL, parseInt(procedureId, 10));
                if (response) {
                    const currentPendingChanges = getPendingChanges();
                    const baseProcedures = currentPendingChanges.procedures || product.procedures || [];

                    // Check if procedure already exists
                    if (baseProcedures.some((p: any) => p.id === response.id)) {
                        return;
                    }

                    const newProcedure: Procedure = {
                        id: response.id,
                        lastUpdatedUtc: response.lastUpdatedUtc,
                        title: response.title,
                        description: response.description,
                        comments: response.comments,
                        filePath: response.filePath,
                        category: PROCEDURE_CATEGORIES.OPERATIONAL,
                        score: response.score,
                    };

                    const updatedProcedures = [...baseProcedures, newProcedure];
                    mergePendingChanges({ procedures: updatedProcedures as any });
                    setSelectedProcedureId('');
                }
            } catch (error) {
                console.error('Failed to add procedure:', error);
            }
        },
        [product.procedures, mergePendingChanges, getPendingChanges, getProcedureDetails]
    );

    if (!product.temperatureRequirements && !showEditUI) {
        return null;
    }

    // Get procedures from pending changes if available, otherwise from product
    const currentProcedures = (showEditUI && getPendingChanges ? getPendingChanges().procedures : null) || product.procedures || [];

    // Operational procedures (Category 3)
    const operationalProcedures = currentProcedures.filter((proc: Procedure) => proc.category === 3) || [];

    // Comments (Category 3)
    const operationalComment = product.comments?.find((comment) => comment.category === 3);
    const hasOperationalComment = operationalComment && operationalComment.text && operationalComment.text.trim() !== '';

    // Comments MSG (Category 13)
    const msgComment = product.comments?.find((comment) => comment.category === 13);
    const hasMsgComment = msgComment && msgComment.text && msgComment.text.trim() !== '';

    return (
        <SectionCard>
            <SectionTitle>Operational and Heating Requirements</SectionTitle>
            <InfoGrid>
                {/* Loading Temperature */}
                <InfoItem>
                    <label>Loading Temperature:</label>
                    {showEditUI ? (
                        <RangeInputWrapper>
                            <CompactRangeInput45
                                type="text"
                                value={formData.loadingTempMin}
                                onChange={(e) => updateTemperatureRequirements('loadingTempMin', e.target.value)}
                                placeholder="Min"
                            />
                            <span>-</span>
                            <CompactRangeInput45
                                type="text"
                                value={formData.loadingTempMax}
                                onChange={(e) => updateTemperatureRequirements('loadingTempMax', e.target.value)}
                                placeholder="Max"
                            />
                            <UnitLabel35>°C</UnitLabel35>
                        </RangeInputWrapper>
                    ) : (
                        <span>{renderTemperatureRange(product.temperatureRequirements?.loadingTemperatureRange)}</span>
                    )}
                </InfoItem>

                {/* Voyage Temperature */}
                <InfoItem>
                    <label>Voyage Temperature:</label>
                    {showEditUI ? (
                        <RangeInputWrapper>
                            <CompactRangeInput45
                                type="text"
                                value={formData.voyageTempMin}
                                onChange={(e) => updateTemperatureRequirements('voyageTempMin', e.target.value)}
                                placeholder="Min"
                            />
                            <span>-</span>
                            <CompactRangeInput45
                                type="text"
                                value={formData.voyageTempMax}
                                onChange={(e) => updateTemperatureRequirements('voyageTempMax', e.target.value)}
                                placeholder="Max"
                            />
                            <UnitLabel35>°C</UnitLabel35>
                        </RangeInputWrapper>
                    ) : (
                        <span>{renderTemperatureRange(product.temperatureRequirements?.voyageTemperatureRange)}</span>
                    )}
                </InfoItem>

                {/* Unloading Temperature */}
                <InfoItem>
                    <label>Unloading Temperature:</label>
                    {showEditUI ? (
                        <RangeInputWrapper>
                            <CompactRangeInput45
                                type="text"
                                value={formData.unloadingTempMin}
                                onChange={(e) => updateTemperatureRequirements('unloadingTempMin', e.target.value)}
                                placeholder="Min"
                            />
                            <span>-</span>
                            <CompactRangeInput45
                                type="text"
                                value={formData.unloadingTempMax}
                                onChange={(e) => updateTemperatureRequirements('unloadingTempMax', e.target.value)}
                                placeholder="Max"
                            />
                            <UnitLabel35>°C</UnitLabel35>
                        </RangeInputWrapper>
                    ) : (
                        <span>{renderTemperatureRange(product.temperatureRequirements?.unloadingTemperatureRange)}</span>
                    )}
                </InfoItem>

                {/* Max Adjacent Temperature */}
                {(product.temperatureRequirements?.maxAdjacentTemperature || showEditUI) && (
                    <InfoItem>
                        <label>Max Adjacent Temperature:</label>
                        {showEditUI ? (
                            <RangeInputWrapper>
                                <CompactRangeInput45
                                    type="text"
                                    value={formData.maxAdjacentTemp}
                                    onChange={(e) => updateTemperatureRequirements('maxAdjacentTemp', e.target.value)}
                                    placeholder="Value"
                                />
                                <UnitLabel35>°C</UnitLabel35>
                            </RangeInputWrapper>
                        ) : (
                            <span>{product.temperatureRequirements?.maxAdjacentTemperature?.value}°C</span>
                        )}
                    </InfoItem>
                )}

                {/* Calculated Maximum Heating - only shown in edit mode as a reference */}
                {showEditUI && (
                    <InfoItem>
                        <label>Calculated Maximum Heating:</label>
                        <span>
                            {product.physicalData?.boilingPointRange?.minValue != null || product.regulations?.uscgGroup != null
                                ? `${calculateMaxAdjacentTemperature(
                                      product.physicalData.boilingPointRange.minValue?.value,
                                      product.regulations.uscgGroup?.number
                                  )}°C`
                                : 'N/A (requires Boiling Point and USCG Group)'}
                        </span>
                    </InfoItem>
                )}
            </InfoGrid>

            {/* Operational Procedures */}
            {operationalProcedures.length > 0 && (
                <FullWidthFields>
                    {operationalProcedures.map((procedure) => (
                        <ProcedureItem key={procedure.id}>
                            <ProcedureHeader>
                                <label>
                                    {procedure.title} ({procedure.id})
                                </label>
                                {showEditUI && (
                                    <DeleteButton onClick={() => removeProcedure(procedure.id)} title="Remove Procedure">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                                        </svg>
                                    </DeleteButton>
                                )}
                            </ProcedureHeader>
                            {procedure.filePath && (
                                <div className="full-width-content">
                                    <a href={`/api/files/Procedure/${procedure.filePath}`} target="_blank" rel="noopener noreferrer">
                                        {procedure.filePath}
                                    </a>
                                </div>
                            )}
                            {procedure.description && (
                                <div className="full-width-content">
                                    <pre>{procedure.description}</pre>
                                </div>
                            )}
                            {procedure.comments && (
                                <div className="full-width-content">
                                    <strong>Comments:</strong>
                                    <pre>{procedure.comments}</pre>
                                </div>
                            )}
                        </ProcedureItem>
                    ))}
                </FullWidthFields>
            )}

            {/* Procedure Selector - shown in edit mode only when no procedures exist (for product categories) */}
            {showEditUI && operationalProcedures.length === 0 && (
                <FullWidthFields>
                    <InfoItem>
                        <label htmlFor="procedure-selector">Select a procedure:</label>
                        <ProcedureSelect
                            id="procedure-selector"
                            title="Select an operational procedure to add"
                            value={selectedProcedureId}
                            onChange={(e) => {
                                setSelectedProcedureId(e.target.value);
                                if (e.target.value) {
                                    addProcedure(e.target.value);
                                }
                            }}
                            disabled={proceduresLoading}
                        >
                            <option value="">-- Select --</option>
                            {availableProcedures
                                .filter((proc) => !operationalProcedures.some((op) => op.id === proc.id))
                                .sort((a, b) => a.title.localeCompare(b.title))
                                .map((proc) => (
                                    <option key={proc.id} value={proc.id}>
                                        {proc.title} ({proc.id})
                                    </option>
                                ))}
                        </ProcedureSelect>
                    </InfoItem>
                </FullWidthFields>
            )}

            {/* Comments Section */}
            {(hasOperationalComment || hasMsgComment || showEditUI) && (
                <FullWidthFields>
                    {/* Operational Comments */}
                    {(hasOperationalComment || showEditUI) && (
                        <FullWidthItem>
                            <label>Comments:</label>
                            {showEditUI ? (
                                <EditableTextarea
                                    value={formData.operationalCommentText}
                                    onChange={(e) => updateComment(3, e.target.value)}
                                    placeholder="Enter operational and heating comments..."
                                    rows={3}
                                />
                            ) : (
                                <div className="full-width-content">
                                    <pre>{operationalComment?.text}</pre>
                                </div>
                            )}
                        </FullWidthItem>
                    )}

                    {/* MSG Comments */}
                    {(hasMsgComment || showEditUI) && (
                        <FullWidthItem>
                            <label>Comments (MSG):</label>
                            {showEditUI ? (
                                <EditableTextarea
                                    value={formData.msgCommentText}
                                    onChange={(e) => updateComment(13, e.target.value)}
                                    placeholder="Enter MSG comments..."
                                    rows={3}
                                />
                            ) : (
                                <div className="full-width-content">
                                    <pre>{msgComment?.text}</pre>
                                </div>
                            )}
                        </FullWidthItem>
                    )}
                </FullWidthFields>
            )}
        </SectionCard>
    );
};

export default OperationalHeatingSection;
